import { FC, MouseEvent, useEffect, useRef, useState } from "react";

import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import { useSnackbar } from "notistack";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { TableCellContainer } from "@/dashboard-design-system/DashboardTableCell/TableCellContainer";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Check, Copy } from "@/icons/outlined";

export interface TableCopyableCellProps {
  value: string;
  valueType: "email" | "phoneNumber" | "url";
  copyLabel: string;
}

export const TableCopyableCell: FC<TableCopyableCellProps> = ({ value, valueType, copyLabel }) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const copiedTimeout = useRef<number | undefined>(undefined);
  const copyAttempt = useRef(0);
  const isCopied = copiedValue === value;
  const parsedPhone =
    valueType === "phoneNumber" ? parsePhoneNumberFromString(value.startsWith("+") ? value : `+${value}`) : undefined;
  const phoneNumber = parsedPhone?.isPossible() ? parsedPhone : undefined;
  const displayValue = phoneNumber?.formatInternational() ?? value;
  const href = valueType === "phoneNumber" ? `tel:${phoneNumber?.number ?? value}` : value;

  useEffect(
    () => () => {
      window.clearTimeout(copiedTimeout.current);
      copyAttempt.current += 1;
    },
    [value]
  );

  const copy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const attempt = ++copyAttempt.current;
    window.clearTimeout(copiedTimeout.current);
    setCopiedValue(null);
    try {
      await navigator.clipboard.writeText(displayValue);
      if (attempt !== copyAttempt.current) {
        return;
      }
      setCopiedValue(value);
      copiedTimeout.current = window.setTimeout(() => setCopiedValue(null), 2_000);
    } catch {
      if (attempt === copyAttempt.current) {
        enqueueSnackbar(t("common", "buttonCopy.linkNotSuccessfullyCopy"), { vibe: "danger" });
      }
    }
  };

  return (
    <TableCellContainer
      layout="inline"
      data-test={valueType === "phoneNumber" ? "table-phone-number-cell" : `table-${valueType}-cell`}
      sx={{
        "&:hover [data-test='table-cell-copy-button'], &:focus-within [data-test='table-cell-copy-button']": {
          opacity: 1,
        },
        "@media (hover: none)": { "& [data-test='table-cell-copy-button']": { opacity: 1 } },
      }}
    >
      {valueType === "email" ? (
        <DashboardTypography variant="body2" vibe="text-neutral-moderate" noWrap sx={{ minWidth: 0 }}>
          {displayValue}
        </DashboardTypography>
      ) : (
        <DashboardLink
          href={href}
          vibe="neutral"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          sx={{
            display: "block",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: valueType === "url" ? "1 0 0" : "0 1 auto",
          }}
        >
          <DashboardTypography variant="body2" vibe="text-neutral-moderate" noWrap component="span">
            {displayValue}
          </DashboardTypography>
        </DashboardLink>
      )}
      <DashboardIconButton
        size="extraSmall"
        vibe="neutral"
        aria-label={isCopied ? t("common", "buttonCopy.copied") : copyLabel}
        data-test="table-cell-copy-button"
        onClick={(event) => void copy(event)}
        sx={{ opacity: isCopied ? 1 : 0 }}
      >
        {isCopied ? <Check /> : <Copy />}
      </DashboardIconButton>
    </TableCellContainer>
  );
};
